import React, { useState, useEffect, useRef } from "react";
import { Form, Input, Row, Col, Button, Select, message } from "antd";
import { updateTransaction } from "../../../../Services/TransactionService";
import axios from "axios";
import { FETCH_ALL_ARTICLE_API } from "../../../../Constants/Apis";
import { LoadingOutlined } from "@ant-design/icons";

const TransactionCreationForm = ({
  modalForm,
  transactionData = {},
  discountData,
  stockData = [],
  userData,
  callReFetchTransaction,
  setIsEditModalVisible,
  isEditMode, // NEW: To distinguish between Create and Edit mode
}) => {
  const [priceDisabled, setPriceDisabled] = useState(true); // Default to disabled
  const originalPriceRef = useRef(null); // Store the original price from stock data
  const [selectedArticle, setSelectedArticle] = useState(null); // Store the selected article data
  const [isLoadingArticle, setIsLoadingArticle] = useState(false); // Loading state for article fetch

  const handleUpdate = () => {
    modalForm
      .validateFields()
      .then((values) => {
        // Extract existing transaction details
        const {
          barcode,
          article,
          size,
          color,
          price,
          quantity,
          discount = 0,
          keterangan,
          total,
          hpp,
        } = values;

        const oldQuantity = transactionData.quantity || 0; // Get previous quantity
        const quantity_diff = oldQuantity - quantity; // Calculate quantity difference

        // Construct the payload for updateTransactionById
        const payload = {
          barcode,
          counterCode: transactionData.counterCode, // Ensure counterCode is included
          discount,
          quantity,
          price,
          hpp, // Include hpp in the payload
          keterangan,
          total,
          quantity_diff, // Needed for stock update
        };

        // Call API to update the transaction
        updateTransaction(transactionData._id, payload)
          .then((response) => {
            message.success("Transaction updated successfully!"); // Show success message
            modalForm.resetFields(); // Reset form fields
            modalForm.setFieldsValue({ barcode: null }); // Ensure barcode is cleared
            modalForm.setFieldsValue({ total: null }); // Ensure total is cleared
            modalForm.setFieldsValue({ discount: null }); // Ensure discount is cleared
            modalForm.setFieldsValue({ keterangan: null }); // Ensure keterangan is cleared
            modalForm.setFieldsValue({ quantity: null }); // Ensure quantity is cleared
            modalForm.setFieldsValue({ price: null }); // Ensure price is cleared
            modalForm.setFieldsValue({ article: null }); // Ensure article is cleared
            modalForm.setFieldsValue({ size: null }); // Ensure size is cleared
            modalForm.setFieldsValue({ color: null }); // Ensure color is cleared
            modalForm.setFieldsValue({ counterCode: null }); // Ensure counterCode is cleared
            modalForm.setFieldsValue({ quantity_diff: null }); // Ensure quantity_diff is cleared
            modalForm.setFieldsValue({ isEditMode: false }); // Ensure edit mode is reset
            modalForm.setFieldsValue({ transactionData: {} }); // Reset transactionData state
            modalForm.setFieldsValue({ modalVisible: false }); // Hide modal after update
            callReFetchTransaction();
            setIsEditModalVisible(false);
          })
          .catch((error) => {
            console.error("Error updating transaction:", error);
            message.error("Failed to update transaction. Please try again.");
          });
      })
      .catch((errorInfo) => {
        console.log("Validation Failed:", errorInfo);
      });
  };

  useEffect(() => {
    if (isEditMode && transactionData) {
      modalForm.setFieldsValue({
        barcode: transactionData.barcode,
        article: transactionData.article,
        size: transactionData.size,
        color: transactionData.color,
        price: transactionData.price,
        quantity: transactionData.quantity,
        discount: transactionData.discount,
        keterangan: transactionData.keterangan,
        total: transactionData.total,
      });
    }
  }, [isEditMode, transactionData, modalForm]);

  // Function to fetch article data by barcode prefix
  const fetchArticleByBarcodePrefix = async (barcodePrefix) => {
    if (!barcodePrefix) return null;

    try {
      // Use the article dropdown API with the barcode prefix as search keyword
      const response = await axios.get(
        `${FETCH_ALL_ARTICLE_API}/dropdown?searchKeyword=${barcodePrefix}`
      );

      // Extract article data from response
      const articles = response?.data?.data || [];

      console.log("Fetched articles count:", articles.length);

      // Find article with matching preBarcode
      const matchedArticle = articles.find(
        (art) => art?.preBarcode === barcodePrefix
      );

      if (matchedArticle) {
        console.log("Found exact match by preBarcode in API response");
        return matchedArticle;
      }

      // If no exact match, try to find a partial match in the API response
      const partialMatch = articles.find(
        (art) => art?.preBarcode && art.preBarcode.includes(barcodePrefix)
      );

      if (partialMatch) {
        console.log("Found partial match by preBarcode in API response");
        return partialMatch;
      }

      console.log("No match found in API response");
      return null;
    } catch (error) {
      console.error("Error fetching article data:", error);
      return null;
    }
  };

  // Function to determine price based on article data
  const determinePriceFromArticle = (article, stockItem) => {
    if (
      !article ||
      !Array.isArray(article.prices) ||
      article.prices.length === 0
    ) {
      console.log(
        "No valid article prices array, using stock price:",
        stockItem.price || 0
      );
      return stockItem.price || 0;
    }

    try {
      // Try to find a price specific to this counter's department and color
      const counterDeptId = userData?.deptId;
      const stockColorId = stockItem.colorId;

      console.log(
        "Looking for price with deptId:",
        counterDeptId,
        "and colorId:",
        stockColorId
      );

      // First try to find a price specific to this department and color
      const specificPrice = article.prices.find(
        (p) => p?.deptId === counterDeptId && p?.colorId === stockColorId
      );

      // If not found, try to find a price for this department (any color)
      const deptPrice = !specificPrice
        ? article.prices.find(
            (p) =>
              p?.deptId === counterDeptId &&
              (!p?.colorId || p?.colorId === null)
          )
        : null;

      // If still not found, use the first price in the array
      if (specificPrice && specificPrice.harga_internal !== undefined) {
        const price = Number(specificPrice.harga_internal);
        console.log("Using specific dept+color price:", price);
        return price;
      } else if (deptPrice && deptPrice.harga_internal !== undefined) {
        const price = Number(deptPrice.harga_internal);
        console.log("Using department price:", price);
        return price;
      } else if (
        article.prices[0] &&
        article.prices[0].harga_internal !== undefined
      ) {
        const price = Number(article.prices[0].harga_internal);
        console.log("Using default price:", price);
        return price;
      }

      // Fallback to stock price if no valid article prices found
      const stockPrice = Number(stockItem.price) || 0;
      console.log(
        "Using fallback stock price (no valid article prices):",
        stockPrice
      );
      return stockPrice;
    } catch (error) {
      console.error("Error determining price:", error);
      const stockPrice = Number(stockItem.price) || 0;
      return stockPrice;
    }
  };

  const handleBarcodeChange = async (value) => {
    if (!value) {
      modalForm.setFieldsValue({
        barcode: null,
        article: null,
        size: null,
        color: null,
        price: null,
        total: null,
        sku: null, // Clear SKU when barcode is cleared
      });
      originalPriceRef.current = null;
      setSelectedArticle(null);
      setIsLoadingArticle(false);
      return;
    }
  
    // Force update the form value immediately
    modalForm.setFieldsValue({ barcode: value });
  
    // Safely handle potential undefined values
    const stockItem = stockData?.find((item) => item?.barcode === value);
    if (stockItem) {
      // Set initial values from stock and show loading state for price
      setIsLoadingArticle(true);
  
      // Populate form with stock data immediately
      modalForm.setFieldsValue({
        article: stockItem.article,
        size: stockItem.size,
        color: stockItem.color,
        price: stockItem.price || 0, // Initial price from stock
        hpp: stockItem.price || 0, // Set hpp as the stock price
        total: null, // Reset total when barcode changes
        sku: stockItem.sku, // Set the SKU from the stock item
      });
      
      // Store the initial stock price as a fallback
      originalPriceRef.current = stockItem.price || 0;
      
      // Rest of the function remains unchanged
      // Extract the first 7 digits of the stock barcode
      const barcodePrefix = stockItem.barcode
        ? stockItem.barcode.substring(0, 7)
        : "";
  
      console.log("Stock barcode:", stockItem.barcode);
      console.log("First 7 digits:", barcodePrefix);
  
      try {
        // Fetch article data by barcode prefix
        const article = await fetchArticleByBarcodePrefix(barcodePrefix);
  
        // Update selected article state
        setSelectedArticle(article);
  
        if (article) {
          console.log("Article found, determining price");
          // Determine price based on article data
          const price = determinePriceFromArticle(article, stockItem);
  
          // Store the price for reference
          originalPriceRef.current = price;
  
          console.log("Setting price in form to:", price);
  
          // First reset the fields to ensure the update is recognized
          modalForm.setFields([
            {
              name: "price",
              value: price,
              touched: true,
              validating: false,
            },
            {
              name: "hpp",
              value: stockItem.price || 0, // Keep hpp as the original stock price
              touched: true,
              validating: false,
            },
          ]);
  
          // Calculate and update total if quantity exists
          const quantity = modalForm.getFieldValue("quantity") || 1;
          if (quantity) {
            const discount = modalForm.getFieldValue("discount") || 0;
            const discountMultiplier = discount ? (100 - discount) / 100 : 1;
            const total = quantity * price * discountMultiplier;
  
            // Update total field
            modalForm.setFields([
              {
                name: "total",
                value: total,
                touched: true,
                validating: false,
              },
            ]);
          }
  
          console.log("Updated price to:", price);
  
          // Force the form to update
          setTimeout(() => {
            // This additional update helps ensure the UI reflects the changes
            modalForm.setFieldsValue({ price: price });
          }, 50);
        } else {
          console.log("No article found, using stock price");
          const stockPrice = stockItem.price || 0;
          originalPriceRef.current = stockPrice;
  
          console.log("Setting stock price in form to:", stockPrice);
  
          // First reset the field to ensure the update is recognized
          modalForm.setFields([
            {
              name: "price",
              value: stockPrice,
              touched: true,
              validating: false,
            },
          ]);
  
          // Calculate and update total
          const quantity = modalForm.getFieldValue("quantity") || 1;
          if (quantity) {
            const discount = modalForm.getFieldValue("discount") || 0;
            const discountMultiplier = discount ? (100 - discount) / 100 : 1;
            const total = quantity * stockPrice * discountMultiplier;
  
            // Update total field
            modalForm.setFields([
              {
                name: "total",
                value: total,
                touched: true,
                validating: false,
              },
            ]);
          }
  
          // Force the form to update
          setTimeout(() => {
            // This additional update helps ensure the UI reflects the changes
            modalForm.setFieldsValue({ price: stockPrice });
          }, 50);
        }
      } catch (error) {
        console.error("Error in barcode change handling:", error);
        // Keep the stock price in case of error
        originalPriceRef.current = stockItem.price || 0;
      } finally {
        setIsLoadingArticle(false);
      }
  
      // Keep price disabled by default when barcode changes
      setPriceDisabled(true);
  
      console.log("Selected stock:", stockItem);
      console.log("Selected article:", selectedArticle);
    } else {
      // Clear fields if barcode not found
      modalForm.setFieldsValue({
        article: null,
        size: null,
        color: null,
        price: null,
        total: null,
      });
      setSelectedArticle(null);
    }
  };

  const handleFormChange = (changedValues, allValues) => {
    const { barcode, discount = 0, quantity = 1, price = 0 } = allValues;

    // Handle discount changes
    if (
      "discount" in changedValues ||
      discount === null ||
      discount === undefined
    ) {
      const stockItem = stockData.find((item) => item.barcode === barcode);
      if (stockItem) {
        const selectedDiscount = discountData.find(
          (d) => d.discountValue === discount
        );

        if (selectedDiscount?.name === "SP") {
          setPriceDisabled(false);
          // Recalculate total with current price and discount
          const discountMultiplier = discount ? (100 - discount) / 100 : 1;
          const total = quantity * price * discountMultiplier;
          modalForm.setFieldsValue({ total });
        } else {
          // For any other discount or when discount is cleared/canceled, reset to original price and disable editing
          const originalPrice = originalPriceRef.current;
          modalForm.setFieldsValue({ price: originalPrice });
          // Recalculate total with original price and new discount
          const discountMultiplier = discount ? (100 - discount) / 100 : 1;
          const total = quantity * originalPrice * discountMultiplier;

          modalForm.setFieldsValue({ total });

          setPriceDisabled(true);
        }
      } else {
        // If no stock item found but discount was changed, ensure price is disabled
        setPriceDisabled(true);
      }
    }

    // When discount is explicitly cleared (set to null/undefined), always disable price editing
    if (
      "discount" in changedValues &&
      (changedValues.discount === null || changedValues.discount === undefined)
    ) {
      if (originalPriceRef.current !== null) {
        // Reset price to original price from stock data
        modalForm.setFieldsValue({ price: originalPriceRef.current });

        // Recalculate total with the original price
        const updatedPrice = originalPriceRef.current;
        const updatedTotal = quantity * updatedPrice;
        modalForm.setFieldsValue({ total: updatedTotal });
      }
      setPriceDisabled(true);
    }

    // Handle barcode changes
    if ("barcode" in changedValues) {
      const stockItem = stockData.find((item) => item.barcode === barcode);
      if (stockItem) {
        // Update original price reference and form price
        originalPriceRef.current = stockItem.harga_internal;
        modalForm.setFieldsValue({ price: stockItem.harga_internal });
        setPriceDisabled(true);
      }
    }

    if ("quantity" in changedValues || "price" in changedValues) {
      const discountMultiplier = discount ? (100 - discount) / 100 : 1;
      const total = quantity * price * discountMultiplier;
      modalForm.setFieldsValue({ total });
    }
  };

  return (
    <Form
      form={modalForm}
      layout="vertical"
      onValuesChange={handleFormChange}
      modalProps={{ maskClosable: false, keyboard: false }}
    >
      <Form.Item
        label="SKU"
        name="barcode"
        rules={[{ required: true, message: "Please input Barcode!" }]}
      >
        <div>
          <Select
            placeholder="Select SKU"
            onChange={handleBarcodeChange}
            showSearch
            optionFilterProp="children"
            value={modalForm.getFieldValue("barcode")} // Ensure it binds to the form value
            allowClear
            disabled={isEditMode} // Disable barcode selection in edit mode
            filterOption={(input, option) =>
              option.children
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            virtual={true}
            listHeight={200}
            style={{ height: "100%", width: "100%" }} // Ensure Select takes full width
          >
            {stockData.map((stock) => (
              <Select.Option key={stock.barcode} value={stock.barcode}>
                {stock.sku} <br></br> ({stock.article} - {stock.color} -{" "}
                {stock.size} )
              </Select.Option>
            ))}
          </Select>
        </div>
      </Form.Item>

      {/* Hidden field to store SKU */}
      <Form.Item name="sku" hidden={true}>
        <Input />
      </Form.Item>

      <Form.Item label="Article" name="article">
        <Input disabled />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Size" name="size">
            <Input disabled />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Color" name="color">
            <Input disabled />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Quantity"
            name="quantity"
            rules={[{ required: true, message: "Please input Quantity!" }]}
          >
            <Input
              type="number"
              min={1}
              onChange={(e) => {
                const value = Number(e.target.value);
              }}
              onWheel={(e) => e.target.blur()}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Harga"
            name="price" // This is crucial for Ant Design to manage the value
            rules={[{ required: true, message: "Please input Harga!" }]} // Assuming Harga means price
          >
            <Input
              prefix="Rp. "
              type="number"
              disabled={priceDisabled} // This should be the only control for editability
              onWheel={(e) => e.target.blur()}
              suffix={isLoadingArticle ? <LoadingOutlined spin /> : null}
              onChange={(e) => {
                const value = e.target.value;
                modalForm.setFieldsValue({ price: value });

                // Update total when price changes
                const quantity = modalForm.getFieldValue("quantity") || 1;
                const discount = modalForm.getFieldValue("discount") || 0;
                const discountMultiplier = discount
                  ? (100 - discount) / 100
                  : 1;
                const total = quantity * value * discountMultiplier;
                modalForm.setFieldsValue({ total });
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Keterangan" name="keterangan">
        <Input />
      </Form.Item>

      <Form.Item label="Total" name="total">
        <Input prefix="Rp. " disabled />
      </Form.Item>

      {isEditMode && (
        <Form.Item>
          <Button type="primary" onClick={() => handleUpdate()}>
            Update Transaction
          </Button>
        </Form.Item>
      )}
    </Form>
  );
};

export default TransactionCreationForm;
